import { useEffect, useId, useState } from 'react';
import type { GroupBase, SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { getType, getTypeList } from '../../sources/type';
import translations from '../../translations';
import { getSharedStyles } from '../../utils/select';
import { useNotificationDispatch } from '../notifications/NotificationProvider';

// Prefixes distinguish a generated-type reference from an existing-type id in the picker option values.
const GENERATED_PREFIX = 'generated';
const EXISTING_PREFIX = 'existing';

type TypeOption = { value: string; label: string | null };

interface Props {
    generatedTypes: { reference: string; developerName: string }[];
    typeReference: string | null;
    typeElementId: string | null;
    isInvalid?: boolean;
    onChange: (selection: { typeReference: string | null; typeElementId: string | null }) => void;
}

const TypeReferencePicker = ({
    generatedTypes,
    typeReference,
    typeElementId,
    isInvalid = false,
    onChange,
}: Props) => {
    const inputId = useId();
    const dispatchNotification = useNotificationDispatch();
    const [existingOption, setExistingOption] = useState<TypeOption | null>(null);
    const [loadingExisting, setLoadingExisting] = useState(false);

    // Resolve the label for an existing-type reference we have not loaded yet (e.g. on first render).
    // biome-ignore lint/correctness/useExhaustiveDependencies: mirrors TypeElementDropdown
    useEffect(() => {
        let cancelled = false;

        if (typeElementId && existingOption?.value !== `${EXISTING_PREFIX}:${typeElementId}`) {
            setLoadingExisting(true);

            getType(typeElementId)
                .then((type) => {
                    if (!cancelled) {
                        setExistingOption({
                            value: `${EXISTING_PREFIX}:${type.id}`,
                            label: type.developerName,
                        });
                    }
                })
                .catch((error) => {
                    dispatchNotification({ type: 'notifyError', payload: { error } });
                })
                .finally(() => {
                    if (!cancelled) {
                        setLoadingExisting(false);
                    }
                });
        }

        return () => {
            cancelled = true;
        };
    }, [typeElementId]);

    const selectedValue: TypeOption | null = typeReference
        ? {
              value: `${GENERATED_PREFIX}:${typeReference}`,
              label:
                  generatedTypes.find((type) => type.reference === typeReference)?.developerName ??
                  '',
          }
        : typeElementId
          ? existingOption
          : null;

    const loadOptions = async (search: string): Promise<GroupBase<TypeOption>[]> => {
        const lowerSearch = search.trim().toLowerCase();

        const generatedOptions = generatedTypes
            .filter((type) => (type.developerName ?? '').toLowerCase().includes(lowerSearch))
            .map((type) => ({
                value: `${GENERATED_PREFIX}:${type.reference}`,
                label: type.developerName,
            }));

        let existingOptions: TypeOption[] = [];

        try {
            const result = await getTypeList({ search, limit: 20 });
            existingOptions = result.map((type) => ({
                value: `${EXISTING_PREFIX}:${type.id}`,
                label: type.developerName,
            }));
        } catch (error) {
            dispatchNotification({ type: 'notifyError', payload: { error } });
        }

        const groups: GroupBase<TypeOption>[] = [];

        if (generatedOptions.length > 0) {
            groups.push({
                label: translations.TYPES_generate_type_generated_types_group,
                options: generatedOptions,
            });
        }

        if (existingOptions.length > 0) {
            groups.push({
                label: translations.TYPES_generate_type_existing_types_group,
                options: existingOptions,
            });
        }

        return groups;
    };

    const onSelect = (option: SingleValue<TypeOption>) => {
        if (!option) {
            onChange({ typeReference: null, typeElementId: null });
            return;
        }

        const value = option.value.slice(option.value.indexOf(':') + 1);

        if (option.value.startsWith(`${EXISTING_PREFIX}:`)) {
            setExistingOption(option);
            onChange({ typeReference: null, typeElementId: value });
        } else {
            onChange({ typeReference: value, typeElementId: null });
        }
    };

    // AsyncSelect caches its loaded options, so remount it whenever the set of generated types changes (e.g. a type is
    // deleted or added) to drop stale options. Renaming a type does not change this key, so it won't disrupt editing.
    const generatedTypesKey = generatedTypes.map((type) => type.reference).join('|');

    return (
        <div className={isInvalid ? 'type-reference-invalid' : undefined}>
            <AsyncSelect
                key={generatedTypesKey}
                inputId={inputId}
                name={inputId}
                classNamePrefix="rs"
                aria-label={translations.TYPES_generate_type_property_references_label}
                styles={getSharedStyles<TypeOption>({
                    menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 'var(--exo-z-index-select-menu-dropdown)',
                    }),
                })}
                loadOptions={loadOptions}
                onChange={onSelect}
                placeholder={translations.TYPES_generate_type_property_references_placeholder}
                value={selectedValue}
                noOptionsMessage={() => 'No results found'}
                isLoading={loadingExisting}
                defaultOptions
            />
        </div>
    );
};

export default TypeReferencePicker;
